import type { CSSProperties, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { GuideId } from '@/features/integrations/guides'

/**
 * Inline getting-started panel for an empty Modules / State / Providers page.
 * Wraps one of the Integrations guides (rendered with the org's slug and token
 * already filled in) under a short banner, so a fresh Business/Enterprise org
 * lands on "here's how to publish your first X" instead of a bare empty state.
 * Gate on GUIDED_ONBOARDING_TIERS at the call site. (The Dashboard's launchpad
 * is components/GettingStarted - a task grid, not an embedded guide.)
 */

/** Guides cross-link each other via onSwitchGuide; on these pages that means leaving for /integrations. */
export function useGuideNavigate(): (id: GuideId) => void {
  const navigate = useNavigate()
  return (id) => navigate(`/integrations/${id}`)
}

export function EmptyGuide({ icon, title, blurb, guideId, children }: {
  icon: string
  title: string
  blurb: string
  /** The guide shown below; the banner's button opens it in Guides & Integrations. */
  guideId: GuideId
  children: ReactNode
}) {
  const goTo = useGuideNavigate()
  return (
    <div className="fu" style={s.wrap}>
      <div style={s.banner}>
        <div style={s.icon} aria-hidden="true">{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.title}>{title}</div>
          <div style={s.blurb}>{blurb}</div>
        </div>
        <button type="button" className="btn btn-sm" style={{ flexShrink: 0 }} onClick={() => goTo(guideId)}>
          All guides →
        </button>
      </div>
      <div style={s.guide}>{children}</div>
    </div>
  )
}

const s: Record<string, CSSProperties> = {
  wrap: { display: 'grid', gap: 22 },
  banner: {
    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px',
  },
  icon: { fontSize: '1.4rem', color: 'var(--text3)', flexShrink: 0, lineHeight: 1 },
  title: { fontFamily: 'var(--head)', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text)' },
  blurb: { fontFamily: 'var(--body)', fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.5, marginTop: 2 },
  guide: { maxWidth: 720 },
}
